import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/lib/api-client";
import { AvailabilityOut, Booking, MarketplaceUnit, MonetizedWindow, PayBookingOut } from "@/lib/types";

export function useMonetizedWindows(schoolId?: string | null) {
  return useQuery({
    queryKey: ["booking-windows", schoolId],
    queryFn: async () =>
      (
        await apiClient.get<MonetizedWindow[]>("/bookings/windows", {
          params: schoolId ? { school_id: schoolId } : undefined,
        })
      ).data,
  });
}

export function useMarketplaceUnits(schoolId: string | null) {
  return useQuery({
    queryKey: ["marketplace-units", schoolId],
    queryFn: async () =>
      (await apiClient.get<MarketplaceUnit[]>(`/bookings/marketplace/${schoolId}/units`)).data,
    enabled: !!schoolId,
  });
}

export function useCreateMonetizedWindow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      unit_id: string;
      day_of_week: number;
      start_time: string;
      end_time: string;
      price_per_hour: number;
      currency?: string;
      active?: boolean;
    }) => (await apiClient.post<MonetizedWindow>("/bookings/windows", payload)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["booking-windows"] }),
  });
}

export function useAvailability(unitId: string | null, date: string | null, schoolId?: string | null) {
  return useQuery({
    queryKey: ["booking-availability", unitId, date, schoolId],
    queryFn: async () =>
      (
        await apiClient.get<AvailabilityOut>("/bookings/availability", {
          params: { unit_id: unitId, date, ...(schoolId ? { school_id: schoolId } : {}) },
        })
      ).data,
    enabled: !!unitId && !!date,
  });
}

export function useBookings() {
  return useQuery({
    queryKey: ["bookings"],
    queryFn: async () => (await apiClient.get<Booking[]>("/bookings")).data,
  });
}

export function useMyBookings() {
  return useQuery({
    queryKey: ["my-bookings"],
    queryFn: async () => (await apiClient.get<Booking[]>("/bookings/mine")).data,
  });
}

export function useCreateBooking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      unit_id: string;
      start_at: string;
      end_at: string;
      school_id?: string;
    }) => (await apiClient.post<Booking>("/bookings", payload)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({ queryKey: ["my-bookings"] });
      queryClient.invalidateQueries({ queryKey: ["booking-availability"] });
    },
  });
}

export function usePayBooking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (bookingId: string) =>
      (await apiClient.post<PayBookingOut>(`/bookings/${bookingId}/pay`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({ queryKey: ["my-bookings"] });
    },
  });
}

export function useCancelBooking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (bookingId: string) =>
      (await apiClient.post<Booking>(`/bookings/${bookingId}/cancel`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({ queryKey: ["my-bookings"] });
      queryClient.invalidateQueries({ queryKey: ["booking-availability"] });
    },
  });
}

export function useScanBooking() {
  return useMutation({
    mutationFn: async (qr_token: string) =>
      (await apiClient.post<Booking>("/bookings/scan", { qr_token })).data,
  });
}
